import { FC, useState, ReactNode, MouseEvent } from 'react';
import MuiMenu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { Box, ListItemIcon, PopoverOrigin, Theme } from '@mui/material';
import Icon from '../icon';
import { createUseStyles } from 'react-jss';
import MenuHeader from './header';
import classNames from 'classnames';
import { IconType } from '../icon/helpers';
import Text from '../text';
import { MenuItemConfig } from './helpers';

export type { MenuItemConfig } from './helpers';

interface Props {
  trigger: ReactNode;
  menuId?: string;
  menuItems?: MenuItemConfig[];
  children?: ReactNode;
  className?: string;
  title?: string;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  hideClose?: boolean;
  openOnHover?: boolean;
  minWidth?: number;
  onChange?: (open: boolean) => void;
}

const useStyles = createUseStyles<string, { minWidth: number }>((theme: Theme) => ({
  trigger: {
    cursor: 'pointer',
    display: 'inline-block',
  },
  menu: {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,
    padding: 0,
    minWidth: ({ minWidth }) => minWidth,
    '& .MuiMenuItem-root': {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      padding: '2px 10px',
      lineHeight: 1,
      '& .MuiListItemIcon-root': {
        width: 16,
        minWidth: 16,
      },
    },
  },
}));

const Menu: FC<Props> = ({
  trigger,
  menuId,
  menuItems = [],
  children,
  className = '',
  title,
  anchorOrigin = {
    vertical: 'bottom',
    horizontal: 'right',
  },
  transformOrigin = {
    vertical: 'bottom',
    horizontal: 'left',
  },
  hideClose = false,
  openOnHover = false,
  minWidth = 300,
  onChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = !!anchorEl;
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    onChange?.(true);
  };
  const handleClose = () => {
    setAnchorEl(null);
    onChange?.(false);
  };

  const handleMouseEnter = (event: MouseEvent<HTMLUListElement>) => {
    if (!openOnHover) {
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const styles = useStyles({ minWidth });

  let MenuComp = children;

  if (menuItems.length) {
    MenuComp = menuItems.map(({ id, label, onClick, icon }) => {
      const hasIcon = !!icon;
      const handleClick = () => {
        handleClose();
        onClick();
      };

      return (
        <MenuItem key={id} onClick={handleClick}>
          {hasIcon && (
            <ListItemIcon>
              <Icon name={icon as IconType} size="small" />
            </ListItemIcon>
          )}
          <Text size="small">{label}</Text>
        </MenuItem>
      );
    });
  }

  const hasTitle = !!title;
  const compClass = classNames(styles.menu, className);

  return (
    <Box>
      <span
        className={styles.trigger}
        id={menuId}
        role="button"
        aria-controls={open ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
      >
        {trigger}
      </span>
      <MuiMenu
        id={menuId}
        anchorEl={anchorEl}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': menuId,
        }}
        slotProps={{
          paper: { className: compClass, elevation: 1 },
        }}
        autoFocus={false}
      >
        {hasTitle && <MenuHeader title={title} onClose={handleClose} hideClose={hideClose} />}
        {MenuComp}
      </MuiMenu>
    </Box>
  );
};

export default Menu;
